"use client"

import { useMemo, useState } from "react"
import { useTagSetStore } from "@/lib/tag-set-store"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { FolderPlus, Trash2, Plus, X } from "@/lib/icons"
import { useTranslations } from "next-intl"

const cleanTag = (tag: string) => tag.trim().toLowerCase().replace(/\s+/g, "_")

/**
 * Settings > Tags > Cleanup. Two jobs, unchanged:
 *  1. Repeated tags: a tag saved in more than one set. Scope "All sets": "Remove extra copies" keeps it in the first set and takes it out of
 *     the others. Scope "Current set": lists only the tags the current set shares with another set, and removes one from the current set only.
 *  2. Bundle into a preset: pick tags (from your sets, or typed in) and save them as a Tag Preset, loaded later from the sets bar.
 */
export function TagCleanupTool() {
  const t = useTranslations("tagCleanupTool")
  const { sets, activeSetId, removeTagFromSet, saveTagPreset } = useTagSetStore()
  const [scope, setScope] = useState<"all" | "current">("all")
  const [selected, setSelected] = useState<string[]>([])
  const [presetName, setPresetName] = useState("")
  const [search, setSearch] = useState("")
  const [saved, setSaved] = useState(false)

  const { allTags, duplicates } = useMemo(() => {
    const where = new Map<string, string[]>()
    for (const set of sets) {
      for (const tag of set.tags) {
        const key = tag.toLowerCase()
        const list = where.get(key) ?? []
        if (!list.includes(set.id)) list.push(set.id)
        where.set(key, list)
      }
    }
    const dupes = [...where.entries()].filter(([, ids]) => ids.length > 1).map(([tag, ids]) => ({ tag, setIds: ids }))
    return { allTags: [...where.keys()].sort(), duplicates: dupes }
  }, [sets])

  const currentSet = sets.find((s) => s.id === activeSetId)
  // Current set: only the repeated tags that this set holds.
  const listed = scope === "current" ? duplicates.filter((d) => currentSet && d.setIds.includes(currentSet.id)) : duplicates

  const removeFromCurrent = (tag: string) => {
    if (!currentSet) return
    for (const own of currentSet.tags.filter((x) => x.toLowerCase() === tag)) removeTagFromSet(currentSet.id, own)
  }

  const removeExtraCopies = (tag: string, setIds: string[]) => {
    // Sets may store the tag with different capitals ("Feet"); remove each set's own spelling.
    for (const id of setIds.slice(1)) {
      const set = sets.find((s) => s.id === id)
      for (const own of set?.tags.filter((x) => x.toLowerCase() === tag) ?? []) removeTagFromSet(id, own)
    }
  }

  const term = cleanTag(search)
  const shown = term ? allTags.filter((tag) => tag.includes(term)) : allTags
  const canAddTyped = !!term && !allTags.includes(term) && !selected.includes(term)

  const toggle = (tag: string) => setSelected((cur) => (cur.includes(tag) ? cur.filter((x) => x !== tag) : [...cur, tag]))
  const addTyped = () => {
    if (!canAddTyped) return
    setSelected((cur) => [...cur, term])
    setSearch("")
  }
  const selectSimilar = (tag: string) => {
    const base = tag.split("_")[0]
    setSelected((cur) => Array.from(new Set([...cur, ...allTags.filter((x) => x.includes(base))])))
  }

  const savePreset = () => {
    if (selected.length === 0 || !presetName.trim()) return
    saveTagPreset(presetName.trim(), selected)
    setSelected([])
    setPresetName("")
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <h4 className="text-xs font-semibold">{t("repeatedTags")}</h4>
        <Tabs value={scope} onValueChange={(v) => setScope(v as "all" | "current")}>
          <TabsList className="grid h-8 w-full grid-cols-2">
            <TabsTrigger value="all" className="text-xs">{t("scopeAll")}</TabsTrigger>
            <TabsTrigger value="current" className="text-xs">{t("scopeCurrent")}</TabsTrigger>
          </TabsList>
        </Tabs>
        {listed.length === 0 ? (
          <p className="text-xs text-muted-foreground">{scope === "current" ? t("noRepeatedInCurrent") : t("noRepeatedTags")}</p>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">{scope === "current" ? t("repeatedTagsHelpCurrent") : t("repeatedTagsHelp")}</p>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {listed.map(({ tag, setIds }) => (
                <div key={tag} className="flex items-center justify-between gap-2 rounded-md border px-2 py-1 text-xs">
                  <span className="truncate">
                    {tag} <span className="text-muted-foreground">{t("inSets", { count: setIds.length })}</span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs shrink-0"
                    onClick={() => (scope === "current" ? removeFromCurrent(tag) : removeExtraCopies(tag, setIds))}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    {scope === "current" ? t("removeFromThisSet") : t("removeExtraCopies")}
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      <section className="space-y-2">
        <h4 className="text-xs font-semibold">{t("bundleIntoPreset")}</h4>
        <div className="flex gap-2">
          <Input
            id="cleanup-tag-search"
            placeholder={t("searchOrTypeTag")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addTyped()}
            className="h-8 text-sm"
          />
          <Button size="sm" className="h-8 px-2" onClick={addTyped} disabled={!canAddTyped} aria-label={t("addTypedTag")}>
            <Plus className="h-3 w-3" />
          </Button>
        </div>
        {canAddTyped && <p className="text-xs text-muted-foreground">{t("pressEnterToAdd", { tag: term })}</p>}

        <div className="rounded-md border max-h-48 overflow-y-auto">
          {shown.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-3">{allTags.length === 0 ? t("noSetTags") : t("noTagsFound")}</p>
          ) : (
            shown.map((tag) => (
              <div key={tag} className="flex items-center justify-between gap-2 px-2 py-1 hover:bg-accent/20">
                <label className="flex items-center gap-2 min-w-0 cursor-pointer">
                  <Checkbox checked={selected.includes(tag)} onCheckedChange={() => toggle(tag)} />
                  <span className="text-xs truncate">{tag}</span>
                </label>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs shrink-0" onClick={() => selectSimilar(tag)}>
                  {t("selectSimilar")}
                </Button>
              </div>
            ))
          )}
        </div>

        {selected.length > 0 && (
          <div className="space-y-2 rounded-md border p-2">
            <div className="flex flex-wrap gap-1">
              {selected.map((tag) => (
                <span key={tag} className="inline-flex items-center gap-1 rounded bg-accent/30 px-1.5 py-0.5 text-xs">
                  {tag}
                  <button type="button" aria-label={t("unselect", { tag })} onClick={() => toggle(tag)} className="opacity-70 hover:opacity-100">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
            <Input
              id="cleanup-preset-name"
              placeholder={t("eGFeetTags")}
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && savePreset()}
              className="h-8 text-sm"
            />
            <Button onClick={savePreset} size="sm" className="h-8 w-full" disabled={!presetName.trim()}>
              <FolderPlus className="h-4 w-4 mr-1" />
              {t("saveAsTagPreset")} ({selected.length})
            </Button>
          </div>
        )}
        {saved && <p className="text-xs text-green-600 dark:text-green-400">{t("presetSaved")}</p>}
        <p className="text-xs text-muted-foreground">{t("presetWhereToLoad")}</p>
      </section>
    </div>
  )
}
